import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import useLocalization from '../../hooks/useLocalization';
import useMarketplaceCurrency from '../../hooks/usePlayerCurrency';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { useForfeitFirstDepositBonus } from '../../mutations/useForfeitFirstDepositBonus';
import { FORFEIT_BONUS_DIALOG_SKIN_STYLES } from '../../styles/components/forfeitBonusDialog';
import type { FirstDepositBonusResponse } from '../../types/bonus';
import { CampaignProduct } from '../../types/bonus';
import { formatBonusAmount, getFirstDepositBonusProgressPercent } from '../../utils/bonus';
import { mergeSx } from '../../utils/styles';

type ForfeitBonusDialogProps = {
  bonus: FirstDepositBonusResponse;
  open: boolean;
  productType: CampaignProduct;
  onClose: () => void;
  onCancel?: () => void;
  onSuccess?: (bonus: FirstDepositBonusResponse | null) => void;
};

const ForfeitBonusDialog = ({ bonus, open, productType, onClose, onCancel, onSuccess }: ForfeitBonusDialogProps) => {
  const { t, i18n } = useLocalization();
  const currency = useMarketplaceCurrency();
  const styles = useSkinStyles(FORFEIT_BONUS_DIALOG_SKIN_STYLES);
  const handleCancel = onCancel ?? onClose;
  const { mutate: forfeitBonus, isPending: isLoading } = useForfeitFirstDepositBonus({
    onSuccess: (updatedBonus) => {
      if (onSuccess) {
        onSuccess(updatedBonus);
        return;
      }

      onClose();
    },
  });

  const progress = Math.floor(getFirstDepositBonusProgressPercent(bonus, productType));

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleCancel}
      maxWidth='xs'
      fullWidth
      slotProps={{
        paper: { sx: styles.dialogPaper },
        backdrop: { sx: styles.backdrop },
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>{t('forfeitBonusWarningTitle')}</DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Stack sx={styles.messageContainer}>
          <Typography variant='body2' sx={styles.dialogMessage}>
            {t('forfeitUnlockBonusWarning', {
              progress,
              amount: formatBonusAmount(bonus.bonusPayoutAmount, currency, i18n.language),
            })}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Stack sx={styles.actionRow}>
          <Button
            color='inherit'
            disabled={isLoading}
            variant='outlined'
            sx={mergeSx(styles.actionButton, styles.cancelButton)}
            onClick={handleCancel}
          >
            {t('cancel')}
          </Button>
          <Button
            color='primary'
            disabled={isLoading || !bonus.id}
            variant='contained'
            sx={mergeSx(styles.actionButton, styles.confirmButton)}
            onClick={() => {
              if (bonus.id) forfeitBonus(bonus.id);
            }}
          >
            {t('forfeitBonus')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ForfeitBonusDialog;
